<script setup>
import { ref, onMounted } from 'vue';
import { setMeta } from '@/utils/util';
import * as Diff from 'diff';

onMounted(() => {
  document.title = "文本对比工具 - 在线文本差异比较";
  setMeta("description", "免费在线文本对比工具，支持逐字、逐词、逐行比较，实时高亮显示文本差异。适用于代码比较、文档修订等场景。")
  setMeta("keywords", "文本对比,文本比较,差异检查,文本对照,内容比对")
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "文本对比工具",
    "description": "在线文本差异比较工具，支持实时高亮显示",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CNY"
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
});

const text1 = ref('');
const text2 = ref('');
const diffResult = ref([]);
const diffMode = ref('chars'); // 'chars', 'words', 'lines'

const computeDiff = () => {
  let diff;
  switch (diffMode.value) {
    case 'chars':
      diff = Diff.diffChars(text1.value, text2.value);
      break;
    case 'words':
      diff = Diff.diffWords(text1.value, text2.value);
      break;
    case 'lines':
      diff = Diff.diffLines(text1.value, text2.value);
      break;
  }
  diffResult.value = diff;
};

const clearTexts = () => {
  text1.value = '';
  text2.value = '';
  diffResult.value = [];
};

const getExampleTexts = () => {
  text1.value = '你好，世界！\n这是一段示例文本。\n用于演示文本比较功能。';
  text2.value = '你好，世界！\n这是一段范例文字。\n用来展示差异对比功能。';
  computeDiff();
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">文本对比工具</h1>
    
    <div class="controls">
      <el-radio-group v-model="diffMode" @change="computeDiff">
        <el-radio-button label="chars">逐字比较</el-radio-button>
        <el-radio-button label="words">逐词比较</el-radio-button>
        <el-radio-button label="lines">逐行比较</el-radio-button>
      </el-radio-group>
      
      <div class="buttons">
        <el-button @click="computeDiff" type="primary">比较</el-button>
        <el-button @click="clearTexts">清空</el-button>
        <el-button @click="getExampleTexts">加载示例</el-button>
      </div>
    </div>

    <div class="input-area">
      <div class="text-input">
        <label>原始文本：</label>
        <el-input
          v-model="text1"
          type="textarea"
          :rows="10"
          placeholder="在此输入原始文本..."
          @input="computeDiff"
        />
      </div>
      
      <div class="text-input">
        <label>修改文本：</label>
        <el-input
          v-model="text2"
          type="textarea"
          :rows="10"
          placeholder="在此输入修改后的文本..."
          @input="computeDiff"
        />
      </div>
    </div>

    <div class="diff-result">
      <h3>对比结果：</h3>
      <div class="diff-content">
        <template v-for="(part, index) in diffResult" :key="index">
          <span
            :class="{
              'diff-added': part.added,
              'diff-removed': part.removed,
              'diff-normal': !part.added && !part.removed
            }"
          >{{ part.value }}</span>
        </template>
      </div>
    </div>

    <div class="introduce">
      <h2>什么是文本对比工具？📝</h2>
      <p>
        文本对比工具是一个帮助您比较两段文本之间差异的实用工具。它可以帮助您：
      </p>
      
      <h3>功能特点</h3>
      <ul>
        <li>支持逐字、逐词、逐行三种比较模式</li>
        <li>高亮显示新增和删除的内容</li>
        <li>实时比较，即时查看差异</li>
        <li>清晰直观的差异展示</li>
      </ul>

      <h3>使用场景</h3>
      <ul>
        <li>代码片段比对</li>
        <li>文本修改审查</li>
        <li>文章查重</li>
        <li>文档版本对照</li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.controls {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.buttons {
  display: flex;
  gap: 10px;
}

.input-area {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.text-input {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: bold;
    color: #606266;
  }
}

.diff-result {
  margin-top: 30px;
  
  h3 {
    margin-bottom: 15px;
    color: #303133;
  }
}

.diff-content {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  white-space: pre-wrap;
  font-family: monospace;
  line-height: 1.5;
}

.diff-added {
  background-color: #e6ffe6;
  color: #0a3d0a;
}

.diff-removed {
  background-color: #ffe6e6;
  color: #3d0a0a;
  text-decoration: line-through;
}

.diff-normal {
  color: #606266;
}

.introduce {
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #606266;
    }
  }
}

@media (max-width: 768px) {
  .input-area {
    grid-template-columns: 1fr;
  }
}
</style> 